
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Image,
  Row,
  Select,
  Spin,
  message,
  Input,
  Typography,
  Upload,
} from "antd";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "rut.js";
import Cookies from "universal-cookie";
import { getCookie } from '../../services/cookies';
import { patientsService } from "../../services/patient";
import {
  checkIfReevaluationFinish,
  createReevaluation,
  getPrescriptionsConfig,
  getReevaluationData,
  saveReevaluation
} from '../../services/reservation';
import { getAsset } from '../../services/general';
import { getInterconsultaExams } from '../../services/exam';
import { consultationService } from '../../services/medicalConsultation';
import AvailableDocuments from "./components/AvailableDocuments";
import UploadDocumentsModal from "./components/UploadDocumentsModal";
import countrySettings from './countrySettings.json';
import { missingFile, allowedFileCriteria, uploadAssetController } from '../../utils/fileUtils';
import { createNote, uploadNoteDocument } from '../../services/notes';

const { Option } = Select;
const { TextArea } = Input;

const allowedContentTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
];

const PatientReevaluationSettings = () => {
  const cookie = new Cookies();
  const branchOfficeId = parseInt(cookie.get('branch_office_id'));
  const countryCode = getCookie("country_code");
  const renderReviewConfirmation = countrySettings.reevaluations.renderReviewConfirmation[countryCode];
  const renderInterconsultationNotes = countrySettings.reevaluations.renderInterconsultationNotes[countryCode];
  const allowEmptyExams = countrySettings.reevaluations.allowEmptyExams[countryCode];
  const showInterconsultaExams = countrySettings.reevaluations.showInterconsultaExams[countryCode];
  const showAttendedInPersonCheckbox = countrySettings.reevaluations.showAttendedInPersonCheckbox[countryCode];
  const showDocuments = countrySettings.documentsModal.showDocuments?.[countryCode] ?? true;
  const navigate = useNavigate();
  const { medicalConsultationId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState({
    fetch: false,
    sendForm: false,
    downloadPdf: false,
    documentApproval: false,
    rejectionList: false,
    confirmRevision: false,
    optionalPrescriptionKeys: false,
    photo: false,
  });
  const [reevaluationData, setReevaluationData] = useState();
  const [patientData, setPatientData] = useState();
  const [servicesList, setServicesList] = useState([]);
  const [prescriptionItems, setPrescriptionItems] = useState([]);
  const [prescriptionIndex, setPrescriptionIndex] = useState([]);
  const [optionalPrescriptionKeys, setOptionalPrescriptionKeys] = useState(null);
  const [modalVisible, setModalVisible] = useState({
    open: false,
    success: false,
  });
  const [isFinish, setIsFinish] = useState();
  const [docsRequired, setDocsRequired] = useState(false);
  const [allDocsStatus, setAllDocsStatus] = useState({
    reviewed: false,
    accepted: false,
  });
  const [patientPhotoUrl, setPatientPhotoUrl] = useState();
  const [noteText, setNoteText] = useState("");
  const [noteFiles, setNoteFiles] = useState([]);
  const [interconsultaExamsList, setInterconsultaExamsList] = useState([]);
  const [mcdPrescriptions, setMcdPrescriptions] = useState([]);
  const [prescriptionAssignments, setPrescriptionAssignments] = useState([]);
  const [attendedInPerson, setAttendedInPerson] = useState(false);
  const attendedPrescriptionExamIdsRef = useRef([]);
  const { t } = useTranslation();

  const fetchPrescriptionsConfig = useCallback(async () => {
    try {
      setLoading(loading => { return { ...loading, optionalPrescriptionKeys: true } });

      const response = await getPrescriptionsConfig();

      if (response && response?.optionalDocsKeys) {
        setOptionalPrescriptionKeys(response?.optionalDocsKeys || []);
      }
    } catch (error) {
      console.error('Error en fetchPrescriptionsConfig: ', error);
      message.error({
        content: 'Error al obtener prescripciones opcionales',
        style: {
          marginTop: '40vh',
        },
      });
      setOptionalPrescriptionKeys([]);
    } finally {
      setLoading(loading => { return { ...loading, optionalPrescriptionKeys: false } });
    }
  }, []);


  const getPatientPhoto = async (id) => {
    try {
      setLoading(loading => { return { ...loading, photo: true } });
      const response = await getAsset({ id, type: 'Patient', field: 'document' });
      setPatientPhotoUrl(response?.document_url)
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error al obtener la foto del paciente: ${error}`,
        style: {
          marginTop: '40vh'
        }
      });
    } finally {
      setLoading(loading => { return { ...loading, photo: false } });
    }
  }

  const patientPhoto = () => {
    const missing = missingFile;
    let idCardTemplate = <Image
      src={missing}
      alt="Documento de identidad del paciente"
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />;
    if (patientPhotoUrl && !patientPhotoUrl.includes('missing.png')) {
      idCardTemplate =
        <Image
          src={patientPhotoUrl}
          alt={`Documento de identidad del ${t('medicalConsultations.header.patientTitle').toLocaleLowerCase()}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
    }
    return idCardTemplate;
  }

  useEffect(() => {
    if (renderReviewConfirmation) {
      fetchPrescriptionsConfig();
    }
  }, [fetchPrescriptionsConfig, renderReviewConfirmation]);


  useEffect(() => {
    if (renderReviewConfirmation && prescriptionItems?.length && optionalPrescriptionKeys?.length) {
      if (!showDocuments) {
        setDocsRequired(false);
        return;
      }
      const notEnabledPrescriptions = prescriptionItems.filter(item => !optionalPrescriptionKeys.includes(item.key));

      if (notEnabledPrescriptions.length) {
        const areDocsRequired = notEnabledPrescriptions.some(item => item.documents?.length > 0);

        if (!areDocsRequired) {
          setDocsRequired(false);

          return;
        }

        setDocsRequired(true);

        const areAllReviewed = notEnabledPrescriptions.every(item => item.documents.length === 0 || (item.documents.every(elem => ['accepted', 'rejected'].includes(elem.state))));
        const areAllAccepted = notEnabledPrescriptions.every(item => item.documents.length === 0 || (item.documents.every(elem => elem.state === 'accepted')));

        setAllDocsStatus({ reviewed: areAllReviewed, accepted: areAllAccepted });
      }
    }
  }, [prescriptionItems, renderReviewConfirmation, optionalPrescriptionKeys, showDocuments]);


  const fetchReevaluationData = useCallback(async (params) => {
    setLoading(loading => { return { ...loading, fetch: true } });
    try {
      params.isFinish = isFinish.is_finish
      const response = await getReevaluationData(params);

      if (!response?.patient_id) {
        throw new Error('Ha ocurrido un error al obtener los datos del paciente')
      }

      const responsePatient = await patientsService.getPatientData({ patientId: response['patient_id'] });
      setReevaluationData(response)
      setPatientData({
        document_type: responsePatient[0].document_type,
        document_number: responsePatient[0].document_number,
        first_name: responsePatient[0].names,
        last_name: responsePatient[0].last_name,
      })
      if (!patientPhotoUrl) {
        getPatientPhoto(responsePatient[0]?.id);
      }

      const deferredExamData = response.deferred_exam_extra_data?.find(item => item.selected_exam_ids);
      const admittedExamIds = deferredExamData?.admitted_exam_ids || [];
      const prescriptionExtraData = response.prescription_extra_data || [];

      if (response['selected_prestations']?.length > 0) {
        const renamedItems = response['selected_prestations'].map((item) => ({
          ...item,
          type: item.type === 'Battery' ? 'battery' : 'exam',
        }));

        const pendingDeferredIds = deferredExamData
          ? deferredExamData.selected_exam_ids.filter(id => !admittedExamIds.includes(id))
          : [];

        const individualIds = showInterconsultaExams
          ? []
          : prescriptionExtraData
            .filter(item => item.examId)
            .flatMap(item => Array.isArray(item.examId) ? item.examId.map(id => parseInt(id)) : [parseInt(item.examId)]);

        const allSelectedIds = [...new Set([...pendingDeferredIds, ...individualIds])];

        const selectedPrescriptionService = renamedItems.filter((service) =>
          allSelectedIds.includes(parseInt(service.id))
        );

        setServicesList(renamedItems);
        form.setFieldsValue({
          services: selectedPrescriptionService.map((service) => parseInt(service.id)),
        });
      }

      if (prescriptionExtraData.length > 0) {
        const items = prescriptionExtraData
          .filter(item => item.examId)
          .map(item => ({
            key: item.key,
            name: item.name,
            examId: item.examId,
            documents: item.documents,
          }));
        setPrescriptionItems(items);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error al obtener los datos del paciente: ${error}`,
        style: {
          marginTop: '40vh'
        }
      });
    } finally {
      setLoading(loading => ({ ...loading, fetch: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinish, form]);

  const confirmRefreshPage = event => {
    event.preventDefault();

    const message = '¿Estás seguro que deseas abandonar?';

    return event.returnValue = message;
  };

  const fetchCheckIfFinish = useCallback(async (params) => {
    setLoading(loading => { return { ...loading, fetch: true } });

    setLoading(loading => { return { ...loading, fetch: true } });

    const response = await checkIfReevaluationFinish({ medicalConsultationId })
    setIsFinish(response);

    setLoading(loading => { return { ...loading, fetch: false } });
  }, [medicalConsultationId]);

  useEffect(() => {
    if (isFinish) {
      fetchReevaluationData({ medicalConsultationId });
    }
    window.addEventListener('beforeunload', confirmRefreshPage);

    return () => {
      window.removeEventListener('beforeunload', confirmRefreshPage);
    }
  }, [fetchReevaluationData, isFinish, medicalConsultationId]);

  useEffect(() => {
    fetchCheckIfFinish({ medicalConsultationId });
  }, [fetchCheckIfFinish, medicalConsultationId]);

  useEffect(() => {
    if (!showInterconsultaExams) return;
    const fetchInterconsultaExams = async () => {
      try {
        const response = await getInterconsultaExams();
        setInterconsultaExamsList(response);
      } catch (error) {
        message.error(`Error al obtener los exámenes de interconsulta: ${error}`);
        console.error('Error al obtener exámenes de interconsulta:', error);
      }
    };
    fetchInterconsultaExams();
  }, [showInterconsultaExams]);

  useEffect(() => {
    if (!showInterconsultaExams || !isFinish || isFinish.is_finish || !reevaluationData) return;
    const targetMedicalConsultationDetailId = reevaluationData.latest_descendant_id || medicalConsultationId;
    const fetchMcdPrescriptions = async () => {
      try {
        const response = await consultationService.getGeneratedPrescriptions({
          medical_consultation_detail_id: targetMedicalConsultationDetailId,
          exclude_finalized: true,
        });
        const prescriptions = response || [];
        setMcdPrescriptions(prescriptions);
        setPrescriptionAssignments(
          prescriptions.map(prescription => ({
            prescriptionId: prescription.id,
            attended: false,
            examId: null,
          }))
        );
      } catch (error) {
        message.error(`Error al obtener las prescripciones de la consulta: ${error}`);
        console.error('Error al obtener prescripciones de la consulta:', error);
      }
    };
    fetchMcdPrescriptions();
  }, [showInterconsultaExams, isFinish, reevaluationData, medicalConsultationId]);

  useEffect(() => {
    if (!showInterconsultaExams) return;
    const attendedExamIds = [...new Set(
      prescriptionAssignments
        .filter(assignment => assignment.attended)
        .flatMap(assignment => {
          const prescription = mcdPrescriptions.find(item => item.id === assignment.prescriptionId);
          return prescription?.exam_ids || [];
        })
    )];

    const previouslyAdded = attendedPrescriptionExamIdsRef.current;
    const currentServices = form.getFieldValue('services') || [];
    const withoutPrevious = currentServices.filter(id => !previouslyAdded.includes(id));
    const newServices = [...new Set([...withoutPrevious, ...attendedExamIds])];
    form.setFieldsValue({ services: newServices });
    attendedPrescriptionExamIdsRef.current = attendedExamIds;
  }, [prescriptionAssignments, mcdPrescriptions, showInterconsultaExams, form]);

  const toggleAttended = (prescriptionId, attended) => {
    setPrescriptionAssignments(previous => previous.map(assignment =>
      assignment.prescriptionId === prescriptionId
        ? { ...assignment, attended }
        : assignment
    ));
  };

  const setAssignedExam = (prescriptionId, examId) => {
    setPrescriptionAssignments(previous => previous.map(assignment =>
      assignment.prescriptionId === prescriptionId
        ? { ...assignment, examId: examId ?? null }
        : assignment
    ));
  };

  const showModal = (prescriptionIdx) => {
    setModalVisible(prev => ({ ...prev, open: true }));
    setPrescriptionIndex(prescriptionIdx)
  };

  const onFinish = async (values) => {
    const selectedServices = values['services'] || [];

    const emptyAvailableDocuments = showDocuments && prescriptionItems.some(
      item => item.documents?.length > 0 && item.documents?.some(doc => doc.exam_document_id === null)
    );

    if (
      emptyAvailableDocuments
      && !window.confirm('No has cargado todos los archivos, ¿estás seguro que deseas continuar?')
    ) {
      return null;
    }

    if (!allowEmptyExams && selectedServices.length === 0) {
      message.error({
        content: 'Debes seleccionar al menos 1 prestación',
        style: {
          marginTop: '40vh'
        }
      });
    } else {

      const attendedWithoutExam = prescriptionAssignments.filter(assignment => assignment.attended && !assignment.examId);
      if (
        attendedWithoutExam.length > 0
        && noteText.trim() === ""
        && noteFiles.length === 0
        && !window.confirm(t('reservations.reevaluationSettings.prescriptionsMissingNoteConfirm'))
      ) {
        return null;
      }

      try {
        setLoading(loading => { return { ...loading, sendForm: true } });

        let renamedPrestations = [];

        selectedServices.forEach(item => {
          renamedPrestations.push({
            id: item
          });
        });

        const hasPrescriptionAssignments = mcdPrescriptions.length > 0;

        let params = {
          medical_consultation_id: parseInt(
            reevaluationData.latest_descendant_id || medicalConsultationId
          ),
          admission_patient_id: reevaluationData['admission_patient_id'],
          exams: renamedPrestations,
          interconsultaExams: hasPrescriptionAssignments
            ? prescriptionAssignments
              .filter(assignment => assignment.attended && assignment.examId)
              .map(assignment => ({ id: assignment.examId, prescriptionId: assignment.prescriptionId }))
            : [],
          externallyAttendedPrescriptionIds: hasPrescriptionAssignments
            ? prescriptionAssignments
              .filter(assignment => assignment.attended && !assignment.examId)
              .map(assignment => assignment.prescriptionId)
            : [],
          carryOverPrescriptionIds: hasPrescriptionAssignments
            ? prescriptionAssignments
              .filter(assignment => !assignment.attended)
              .map(assignment => assignment.prescriptionId)
            : [],
          admin_id: values.doctorId,
          branch_office_id: branchOfficeId,
          country_code: countryCode,
          attendedInPerson: showAttendedInPersonCheckbox ? attendedInPerson : undefined,
        };

        let reevaluationResponse;
        if (values.save) {
          reevaluationResponse = await saveReevaluation(params);
        } else {
          reevaluationResponse = await createReevaluation(params);
        }

        if (renderInterconsultationNotes && !values.save) {
          const childConsultationDetailId = reevaluationResponse?.createdMedicalConsultationDetailId;
          if (childConsultationDetailId) {
            if (noteText.trim() !== "" || noteFiles.length > 0) {
              const noteBody = {
                parent_id: childConsultationDetailId,
                parent_type: 'medicalConsultation',
                text: noteText,
                tags: ['interconsultation'],
              };
              const noteResponse = await createNote(noteBody);
              const noteId = noteResponse?.id || (Array.isArray(noteResponse) && noteResponse[0]?.id);
              if (noteId) {
                for (const file of noteFiles) {
                  const uploadResponse = await uploadNoteDocument({ note_id: noteId });
                  const documentId = uploadResponse?.id || uploadResponse?.[0]?.id;
                  if (documentId) {
                    await uploadAssetController(
                      {
                        file,
                        object_field: 'document',
                        object_id: documentId,
                        object_type: 'NotesDocuments',
                      },
                      allowedContentTypes,
                    );
                  }
                }
              }
            }
          }
        }


        const messageContent = values.save ? 'Datos guardados exitosamente' : 'Datos guardados para reevaluación exitosamente'
        message.success({
          content: `${messageContent}`,
          style: {
            marginTop: '40vh'
          }
        });
        setLoading(loading => { return { ...loading, sendForm: false } });
        navigate(-1);
      } catch (error) {
        const errorMessage = typeof error === 'string' ? error : `Error interno: ${error}`;
        console.error(`Error al crear reevaluación: ${errorMessage}`, error);
        message.error({
          content: errorMessage,
          style: {
            marginTop: '35vh'
          }
        });
        setLoading(loading => { return { ...loading, sendForm: false } });
      }
    }
  };


  const hasDeferredExam = reevaluationData?.has_deferred_exam;
  const hasPrescriptionsToLift = mcdPrescriptions.length > 0;

  return (
    <Row justify="center" className="patient-reevaluation-edit-wrapper">
      <Col lg={24} xs={24}>
        <Row justify="center">
          {
            (loading.fetch || loading.optionalPrescriptionKeys) ?
              <Spin className="cmt-spin mt-100" />
              :
              (reevaluationData && patientData) &&
              <Fragment>
                <Col lg={24} xs={24} className="medical-consultation-detail__header pt-20 pb-20">
                  <Row>
                    <Col lg={4} xs={24} className="pl-40 mt-15">
                      <Row>
                        <Col>
                          <Button
                            onClick={() => navigate(-1)}
                            className="cmt-button primary non-outline p-0"
                            type="primary"
                          >
                            <i className="icon-system-chevron-left" />
                            Volver
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                    
                    <Col lg={19} xs={22}>
                      <Row>
                        <Col lg={24} xs={24}>
                          <h1 className="text-center mb-10">{t('reservations.reevaluationSettings.title')}</h1>
                        </Col>
                        <Col lg={24} xs={24}>
                          <Row wrap={true} gutter={[20, 0]} align="middle">
                            <Col lg={2} xs={24}>
                              <Spin className="cmt-spin" spinning={loading?.photo}>
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto'
                                }}>
                                  {patientPhoto()}
                                </div>
                              </Spin>
                            </Col>
                            <Col lg={6} xs={24}>
                              <p className="mb-0" style={{ color: '#888' }}>Trabajador:</p>
                              <strong className="text-uppercase">
                                {patientData['first_name']} {patientData['last_name']}
                              </strong>
                            </Col>
                            <Col lg={4} xs={24}>
                              <p className="mb-0" style={{ color: '#888' }}>
                                {patientData.document_type === 'passport' ? 'Pasaporte:' :
                                 patientData.document_type === 'ce' ? 'Carnet de extranjería:' :
                                 `${(patientData.document_type || '').toUpperCase()}:`}
                              </p>
                              <strong>
                                {patientData.document_type === 'rut' ? format(patientData.document_number) : patientData.document_number}
                              </strong>
                            </Col>
                            <Col lg={12} xs={24}>
                              <p className="mb-0" style={{ color: '#888' }}>Examen:</p>
                              <strong>{reevaluationData['prestation_name']}</strong>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
                <Col lg={24} xs={24} className="patient-reevaluation-edit-wrapper__body mt-30">
                  <Row justify="center">
                    <Col lg={22} xs={22}>
                      <Form form={form} onFinish={onFinish} size="large" className="cmt-form">
                        <Row justify="center">
                          <Col lg={20} xs={22}>
                            {
                              isFinish?.is_finish &&
                              <div className="carousel-alert">
                                <div className="m-15">
                                  <Row type="flex" align="middle">
                                    <Col xs={3} sm={3} lg={1} style={{
                                      verticalAlign: 'middle',
                                    }}>
                                    </Col>
                                    <Col xs={18} sm={18} lg={22}>
                                      <Row type="flex" align="middle">
                                        <Col xs={21} sm={21} lg={22}>
                                          <h3 className="carousel-title">{t('reservations.reevaluationSettings.patientStatus')}</h3>
                                        </Col>
                                      </Row>
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                            }
                            {
                              !isFinish?.is_finish &&
                              <Row>
                                <Col>
                                  <h2 className="mb-15">
                                    {t('reservations.reevaluationSettings.serviceStep')}
                                  </h2>
                                </Col>
                                <Col lg={24} xs={24} className="gray-item p-lg-30 p-xs-15">
                                  {showInterconsultaExams && mcdPrescriptions.length > 0 && (
                                    <Row className="mb-20">
                                      <Col lg={24} xs={24}>
                                        <p className="secondary mb-lg-15 mb-xs-15">
                                          {t('reservations.reevaluationSettings.interconsultaTitle')}
                                        </p>
                                        <Alert
                                          type="info"
                                          showIcon
                                          className="mb-15"
                                          message={t('reservations.reevaluationSettings.prescriptionsInfoAlert')}
                                        />
                                        {prescriptionAssignments.map(assignment => {
                                          const prescription = mcdPrescriptions.find(item => item.id === assignment.prescriptionId);
                                          if (!prescription) return null;
                                          const examsAssignedElsewhere = prescriptionAssignments
                                            .filter(other => other.prescriptionId !== assignment.prescriptionId)
                                            .map(other => other.examId)
                                            .filter(Boolean);
                                          return (
                                            <Row key={assignment.prescriptionId} className="mb-10" align="middle" gutter={[10, 0]}>
                                              <Col lg={6} xs={24}>
                                                <Checkbox
                                                  checked={assignment.attended}
                                                  onChange={(event) => toggleAttended(assignment.prescriptionId, event.target.checked)}
                                                >
                                                  {prescription.interconsultation_motive || prescription.prescription_name || prescription.label}
                                                </Checkbox>
                                              </Col>
                                              <Col lg={10} xs={24}>
                                                <Select
                                                  className="cmt-select"
                                                  style={{ width: '100%' }}
                                                  allowClear
                                                  disabled={!assignment.attended}
                                                  placeholder={t('reservations.reevaluationSettings.prescriptionExamPlaceholder')}
                                                  value={assignment.examId || undefined}
                                                  onChange={(value) => setAssignedExam(assignment.prescriptionId, value)}
                                                  options={interconsultaExamsList.map(exam => ({
                                                    value: exam.id,
                                                    label: exam.name,
                                                    disabled: examsAssignedElsewhere.includes(exam.id),
                                                  }))}
                                                />
                                              </Col>
                                              <Col lg={8} xs={24}>
                                              {assignment.attended && !assignment.examId && (
                                                  <Typography.Text type="secondary" className="font-12">
                                                    {t('reservations.reevaluationSettings.prescriptionExamHelperText')}
                                                  </Typography.Text>
                                                )}
                                              </Col>
                                            </Row>
                                          );
                                        })}
                                      </Col>
                                    </Row>
                                  )}
                                  <Row>
                                    <Col lg={24} xs={24}>
                                      <Row justify="space-between" align="middle" className="mb-lg-15 mb-xs-15">
                                        <Col>
                                          <p className="secondary mb-0">
                                            {hasDeferredExam ? 'Prestaciones' : t('reservations.reevaluationSettings.servicesTitle')}
                                          </p>
                                        </Col>
                                        {showAttendedInPersonCheckbox && (
                                          <Col>
                                            <Checkbox
                                              checked={attendedInPerson}
                                              onChange={(event) => setAttendedInPerson(event.target.checked)}
                                            >
                                              {t('reservations.reevaluationSettings.attendedInPersonLabel')}
                                            </Checkbox>
                                          </Col>
                                        )}
                                      </Row>
                                      <Row>
                                        {allowEmptyExams && (
                                          <Col lg={24} xs={24} className="mb-15">
                                            <Alert
                                              type="info"
                                              showIcon
                                              message={t('reservations.reevaluationSettings.examsInfoAlert')}
                                            />
                                          </Col>
                                        )}
                                        <Col lg={24} xs={24}>
                                          <Form.Item name="services">
                                            <Select
                                              mode="multiple"
                                              id="patientServices"
                                              className="cmt-select"
                                              notFoundContent={null}
                                              style={{ width: '100%' }}
                                              placeholder={t('reservations.reevaluationSettings.servicePlaceholder')}
                                              filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())}
                                              options={servicesList?.map((item, index) => {
                                                const deferredExamData = reevaluationData?.deferred_exam_extra_data?.find(entry => entry.selected_exam_ids);
                                                const admittedExamIds = deferredExamData?.admitted_exam_ids || [];
                                                const isAlreadyAdmitted = admittedExamIds.includes(item.id);
                                                return {
                                                  key: `${index}${item.type}`,
                                                  value: parseInt(item.id),
                                                  type: item.type,
                                                  sale_amount: item.sale_amount,
                                                  department_id: item.department_id,
                                                  label: isAlreadyAdmitted ? `${item.name} (Ya admitido)` : item.name,
                                                  disabled: isAlreadyAdmitted,
                                                };
                                              })}
                                            >
                                              {
                                                servicesList?.map((item, index) =>
                                                  <Option
                                                    key={`${index}${item.type}`}
                                                    value={parseInt(item.id)}
                                                    type={item.type}
                                                    sale_amount={item.sale_amount}
                                                    department_id={item.department_id}
                                                  >
                                                    {item.name}
                                                  </Option>
                                                )
                                              }
                                            </Select>
                                          </Form.Item>
                                        </Col>
                                      </Row>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            }
                            {
                              showDocuments && prescriptionItems.length > 0 && (
                                <AvailableDocuments
                                  t={t}
                                  loading={loading}
                                  setLoading={setLoading}
                                  isFinish={isFinish}
                                  showModal={showModal}
                                  countryCode={countryCode}
                                  countrySettings={countrySettings}
                                  docsRequired={docsRequired}
                                  allDocsStatus={allDocsStatus}
                                  reevaluationData={reevaluationData}
                                  prescriptionItems={prescriptionItems}
                                  medicalConsultationId={medicalConsultationId}
                                  fetchReevaluationData={fetchReevaluationData}
                                  renderReviewConfirmation={renderReviewConfirmation}
                                />
                              )
                            }
                            {
                              renderInterconsultationNotes && isFinish && !isFinish.is_finish && hasPrescriptionsToLift && (
                                <Row className="mt-30 gray-item p-lg-30 p-xs-15">
                                  <Col lg={24} xs={24}>
                                    <h2 className="mb-15">{t('reservations.reevaluationSettings.interconsultationTitle')}</h2>
                                    <p className="mb-15">{t('reservations.reevaluationSettings.interconsultationDescription')}</p>
                                    
                                    <TextArea
                                      className="cmt-input mb-15"
                                      rows={4}
                                      value={noteText}
                                      onChange={(event) => setNoteText(event.target.value)}
                                      placeholder={t('reservations.reevaluationSettings.interconsultationPlaceholder')}
                                      maxLength={600}
                                      showCount
                                    />
                                    
                                    <Upload
                                      beforeUpload={(file) => {
                                        if (allowedFileCriteria(t, file, 2, allowedContentTypes, t('notes.documentImagePdfTypeError'))) {
                                          setNoteFiles(previousFiles => [...previousFiles, file]);
                                        }
                                        return false;
                                      }}
                                      onRemove={(file) => {
                                        setNoteFiles(previousFiles => previousFiles.filter(item => item.uid !== file.uid));
                                      }}
                                      fileList={noteFiles}
                                      multiple
                                    >
                                      <Button className="cmt-button secondary">
                                        <i className="icon-system-plus mr-5" />
                                        {t('reservations.reevaluationSettings.interconsultationAttachButton')}
                                      </Button>
                                    </Upload>
                                    <p className="mt-10 mb-0 font-12 text-muted">{t('reservations.reevaluationSettings.interconsultationAllowedTypes')}</p>
                                  </Col>
                                </Row>
                              )
                            }
                            {
                              isFinish && !isFinish.is_finish &&
                              <Row justify="center" className="mt-40 mb-60">
                                {
                                  renderReviewConfirmation && !hasDeferredExam &&
                                  <Col lg={24} xs={24}>
                                    <h2 className="mb-5">3. {t('reservations.reevaluationSettings.initiateReevaluationTitle')}</h2>
                                    <p className="mb-0" dangerouslySetInnerHTML={{ __html: t('reservations.reevaluationSettings.initiateReevaluationText') }} />
                                  </Col>
                                }
                                <Col lg={4} xs={24} className={(renderReviewConfirmation && !hasDeferredExam) ? "mt-40" : "mt-10"}>
                                  <Form.Item name="reevaluate">
                                    <Button
                                      loading={loading.sendForm}
                                      onClick={() => form.setFieldsValue({ "reevaluate": true, "save": false })}
                                      htmlType="submit"
                                      className={(loading.sendForm || loading.confirmRevision || (docsRequired && !allDocsStatus.accepted && !hasDeferredExam)) ? 'cmt-button disabled' : 'cmt-button primary'}
                                      type="primary"
                                      disabled={loading.sendForm || loading.confirmRevision || (docsRequired && !allDocsStatus.accepted && !hasDeferredExam)}
                                      block
                                    >
                                      Reevaluar
                                    </Button>
                                  </Form.Item>
                                </Col>
                              </Row>
                            }
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Fragment>
          }
        </Row>

        <UploadDocumentsModal
          countryCode={countryCode}
          countrySettings={countrySettings}
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          setPrescriptionItems={setPrescriptionItems}
          prescriptionItem={prescriptionItems[prescriptionIndex]}
          prescriptionIndex={prescriptionIndex}
          medicalConsultationId={parseInt(medicalConsultationId)}
          medicalPrescriptionId={reevaluationData?.medical_prescription_id}
          reevaluationData={reevaluationData}
          fetchReevaluationData={fetchReevaluationData}
        />
      </Col>
    </Row>
  )
};

export default PatientReevaluationSettings;